import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Mail, Phone, User, AlertCircle, CheckCircle,
  Clock, Tag, X, FileText, Calendar, Hash, ArrowLeft, Edit2, 
  Eye, UserCheck, UserX, Ban, CheckCircle2, XCircle,
  Building, MapPin, Briefcase, CreditCard, Shield
} from 'lucide-react';
import { 
  adminVerify, listMembers, getMember, updateMember,
  approveMember, rejectMember, activateMember, deactivateMember,
  createCashPayment, createMember, listMembershipPlans
} from '../../services/api';
import toast from 'react-hot-toast';
import AdminCreateMemberForm from '../../components/admin/AdminCreateMemberForm';

const AdminMembersPage = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCashPaymentModal, setShowCashPaymentModal] = useState(false);
  const [cashPaymentAmount, setCashPaymentAmount] = useState('');
  const [cashPaymentDescription, setCashPaymentDescription] = useState('');
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [showCreateMemberForm, setShowCreateMemberForm] = useState(false);

  const [filters, setFilters] = useState({
    name: '',
    email: '',
    mobile: '',
    memberId: '',
    organization: '',
    approvalStatus: '',
    paymentStatus: '',
    membershipStatus: '',
    startDate: '',
    endDate: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });


  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await loadMembershipPlans();
        await refresh();
      } catch (e) {
        navigate('/admin/login');
      }
    };
    init();
  }, [navigate]);

  const loadMembershipPlans = async () => {
    try {
      const plans = await listMembershipPlans();
      setMembershipPlans(Array.isArray(plans) ? plans : []);
    } catch (e) {
      console.error('Failed to load membership plans:', e);
    }
  };

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const data = await listMembers(params);
      
      // Handle response format
      let membersList = [];
      let paginationInfo = null;
      
      if (Array.isArray(data)) {
        membersList = data.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.registrationDate || 0);
          const dateB = new Date(b.createdAt || b.registrationDate || 0);
          return dateB - dateA; // Descending order
        });
      } else if (data.members) {
        membersList = data.members.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.registrationDate || 0);
          const dateB = new Date(b.createdAt || b.registrationDate || 0);
          return dateB - dateA; // Descending order
        });
        paginationInfo = {
          total: data.total || 0,
          totalPages: data.totalPages || 0,
          page: data.page || 1
        };
      } else {
        membersList = [];
      }
      
      setAllMembers(membersList);
      setMembers(membersList);
      
      // Update pagination if data has pagination info
      if (paginationInfo) {
        setPagination(prev => ({
          ...prev,
          total: paginationInfo.total,
          totalPages: paginationInfo.totalPages
        }));
      }
    } catch (e) {
      const errorMsg = e.message || 'Failed to load members';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const handleViewDetails = async (member) => {
    try {
      const fullMember = await getMember(member._id);
      setSelectedMember(fullMember);
      setShowDetails(true);
    } catch (e) {
      toast.error('Failed to load member details');
      setSelectedMember(member);
      setShowDetails(true);
    }
  };

  const handleApprove = async (member) => {
    if (!window.confirm(`Approve membership for ${member.personName || member.organizationName}?`)) return;
    try {
      await approveMember(member._id, 'Admin', false);
      toast.success('Member approved successfully!');
      await refresh();
      // Reload member details if modal is open
      if (showDetails && selectedMember && selectedMember._id === member._id) {
        const updatedMember = await getMember(member._id);
        setSelectedMember(updatedMember);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to approve member');
    }
  };

  const handleReject = async (member) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await rejectMember(member._id, reason, 'Admin');
      toast.success('Member rejected successfully!');
      await refresh();
      // Reload member details if modal is open
      if (showDetails && selectedMember && selectedMember._id === member._id) {
        const updatedMember = await getMember(member._id);
        setSelectedMember(updatedMember);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to reject member');
    }
  };

  const handleActivate = async (member) => {
    if (!window.confirm(`Activate membership for ${member.personName || member.organizationName}?`)) return;
    try {
      await activateMember(member._id);
      toast.success('Member activated successfully!');
      await refresh();
      // Reload member details if modal is open
      if (showDetails && selectedMember && selectedMember._id === member._id) {
        const updatedMember = await getMember(member._id);
        setSelectedMember(updatedMember);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to activate member');
    }
  };

  const handleDeactivate = async (member) => {
    const reason = window.prompt('Enter deactivation reason:');
    if (!reason) return;
    try {
      await deactivateMember(member._id, reason);
      toast.success('Member deactivated successfully!');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Failed to deactivate member');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Approved': { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      'Pending': { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
      'Rejected': { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      'Active': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: UserCheck },
      'Inactive': { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: UserX },
      'Expired': { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle }
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Tag };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatMemberValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (value instanceof Date) {
      return formatDate(value);
    }
    return String(value);
  };

  const handleCreateCashPayment = async () => {
    if (!cashPaymentAmount || parseFloat(cashPaymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setCreatingPayment(true);
    try {
      await createCashPayment({
        memberId: selectedMember._id,
        amount: parseFloat(cashPaymentAmount),
        membershipPlanId: selectedMember.membershipPlanId,
        membershipPlanName: selectedMember.membershipPlanName,
        description: cashPaymentDescription || `Cash payment for ${selectedMember.personName || selectedMember.organizationName}`
      });
      toast.success('Cash payment created successfully!');
      setShowCashPaymentModal(false);
      setCashPaymentAmount('');
      setCashPaymentDescription('');
      await refresh();
      // Reload member details if modal is open
      if (showDetails && selectedMember) {
        const updatedMember = await getMember(selectedMember._id);
        setSelectedMember(updatedMember);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to create cash payment');
    } finally {
      setCreatingPayment(false);
    }
  };

  // Field groups for member details modal
  const fieldGroups = [
    {
      title: 'Personal Details',
      fields: [
        { key: 'customId', label: 'Member ID' },
        { key: 'personName', label: 'Applicant Name' },
        { key: 'representativeName', label: 'Representative Name' },
        { key: 'gender', label: 'Gender' },
        { key: 'email', label: 'Email' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'telephone', label: 'Telephone' },
        { key: 'aadhar', label: 'Aadhar' },
        { key: 'pancard', label: 'PAN Card' }
      ]
    },
    {
      title: 'Organization Details',
      fields: [
        { key: 'organizationName', label: 'Organization Name' },
        { key: 'industry', label: 'Industry' },
        { key: 'natureOfBusiness', label: 'Nature of Business' },
        { key: 'category', label: 'Category' },
        { key: 'region', label: 'Region' },
        { key: 'memberDescription', label: 'Member Description' },
        { key: 'website', label: 'Website' },
        { key: 'gst', label: 'GST' }
      ]
    },
    {
      title: 'Address Details',
      fields: [
        { key: 'location', label: 'Location' },
        { key: 'state', label: 'State' },
        { key: 'district', label: 'District' },
        { key: 'city', label: 'City' },
        { key: 'pincode', label: 'Pincode' }
      ]
    },
    {
      title: 'Membership Details',
      fields: [
        { key: 'membershipPlanName', label: 'Membership Plan' },
        { key: 'membershipType', label: 'Membership Type' },
        { key: 'startDate', label: 'Start Date', format: formatDate },
        { key: 'endDate', label: 'End Date', format: formatDate },
        { key: 'expiryDate', label: 'Expiry Date', format: formatDate },
        { key: 'services', label: 'Services' },
        { key: 'photo', label: 'Photo' }
      ]
    },
    {
      title: 'Status Details',
      fields: [
        { key: 'approvalStatus', label: 'Approval Status' },
        { key: 'paymentStatus', label: 'Payment Status' },
        { key: 'membershipStatus', label: 'Membership Status' },
        { key: 'approvedBy', label: 'Approved By' },
        { key: 'approvedAt', label: 'Approved At', format: formatDate },
        { key: 'registrationDate', label: 'Registration Date', format: formatDate }
      ]
    }
  ];

  const loadMembers = async () => {
    await refresh();
  };

  const handleMemberCreated = async (member) => {
    setShowCreateMemberForm(false);
    toast.success('Member created successfully!');
    // Refresh members list
    await loadMembers();
  };

  return (
    <div className="w-full">
      {/* Create Member Modal */}
      {showCreateMemberForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-stretch sm:items-center justify-end">
          <div className="bg-white shadow-xl w-full sm:w-[92vw] md:w-[80vw] lg:w-[68vw] xl:w-[58vw] 2xl:w-[50vw] max-w-[860px] h-full sm:h-[calc(100vh-32px)] sm:my-4 overflow-y-auto overflow-x-hidden rounded-none sm:rounded-2xl min-w-0">
            <AdminCreateMemberForm
              onSuccess={handleMemberCreated}
              onCancel={() => setShowCreateMemberForm(false)}
            />
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-2">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Members
              </h2>
              <p className="text-sm text-gray-600 mt-1">View and manage all member registrations</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateMemberForm(true)}
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:text-black px-6 py-2.5 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm w-full sm:w-auto"
          >
            <User className="h-5 w-5" />
            Create New Member
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-700 flex-1">{error}</p>
            <button
              onClick={() => setError('')}
              className="ml-4 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
            <p className="text-sm text-green-700 flex-1">{success}</p>
            <button
              onClick={() => setSuccess('')}
              className="ml-4 text-sm text-green-600 hover:text-green-800 font-medium"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
            {Object.values(filters).some(f => f) && (
              <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                {Object.values(filters).filter(f => f).length} active
              </span>
            )}
          </div>
          {Object.values(filters).some(f => f) && (
            <button
              onClick={() => {
                setFilters({
                  name: '',
                  email: '',
                  mobile: '',
                  memberId: '',
                  organization: '',
                  approvalStatus: '',
                  paymentStatus: '',
                  membershipStatus: '',
                  startDate: '',
                  endDate: ''
                });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {/* Name Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Name
            </label>
            <div className="relative">
              <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.name ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search name..."
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
              {filters.name && (
                <button
                  onClick={() => handleFilterChange('name', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Email Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.email ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search email..."
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
              />
              {filters.email && (
                <button
                  onClick={() => handleFilterChange('email', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Mobile Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Mobile
            </label>
            <div className="relative">
              <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.mobile ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search mobile..."
                value={filters.mobile}
                onChange={(e) => handleFilterChange('mobile', e.target.value)}
              />
              {filters.mobile && (
                <button
                  onClick={() => handleFilterChange('mobile', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Member ID Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Member ID
            </label>
            <div className="relative">
              <Hash className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.memberId ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search ID..."
                value={filters.memberId}
                onChange={(e) => handleFilterChange('memberId', e.target.value)}
              />
              {filters.memberId && (
                <button
                  onClick={() => handleFilterChange('memberId', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Organization Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Organization
            </label>
            <div className="relative">
              <Building className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.organization ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search org..."
                value={filters.organization}
                onChange={(e) => handleFilterChange('organization', e.target.value)}
              />
              {filters.organization && (
                <button
                  onClick={() => handleFilterChange('organization', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Approval Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Approval Status
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.approvalStatus}
              onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          {/* Payment Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Payment Status
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
            >
              <option value="">All Payment Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Membership Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Membership Status
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.membershipStatus}
              onChange={(e) => handleFilterChange('membershipStatus', e.target.value)}
            >
              <option value="">All Membership Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Expired">Expired</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              From Date
            </label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              To Date
            </label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              min={filters.startDate}
            />
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Members
            </h3>
            <span className="text-sm text-gray-500">
              {loading ? 'Loading...' : `${members.length} ${members.length === 1 ? 'member' : 'members'}`}
            </span>
          </div>
        </div>
        
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <User className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-2 font-medium">No members found.</p>
              <p className="text-sm text-gray-500">
                {Object.values(filters).some(f => f) 
                  ? 'Try adjusting your filters'
                  : 'No members have registered yet'}
              </p>
            </div>
          ) : (
            members.map(member => (
              <div key={member._id} className="p-6 flex flex-col lg:flex-row lg:items-start justify-between gap-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all group">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 mb-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <h4 className="font-bold text-gray-900 text-lg mb-1">
                        {member.personName || member.organizationName || 'N/A'}
                      </h4>
                      {member.organizationName && member.personName && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Building className="h-3.5 w-3.5" />
                          {member.organizationName}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {getStatusBadge(member.approvalStatus || 'Pending')}
                      {member.membershipStatus && getStatusBadge(member.membershipStatus)}
                      {member.paymentStatus && (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          member.paymentStatus.toLowerCase() === 'completed' 
                            ? 'bg-green-100 text-green-700 border-green-200' 
                            : member.paymentStatus.toLowerCase() === 'pending'
                            ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                            : 'bg-red-100 text-red-700 border-red-200'
                        }`}>
                          <CreditCard className="h-3 w-3" />
                          {member.paymentStatus.charAt(0).toUpperCase() + member.paymentStatus.slice(1).toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-3">
                    {member.customId && (
                      <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                        <Hash className="h-3.5 w-3.5 text-gray-500" />
                        <span className="font-semibold text-gray-700">ID:</span> 
                        <span className="text-gray-600">{member.customId}</span>
                      </span>
                    )}
                    {member.email && (
                      <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                        <Mail className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-gray-600">{member.email}</span>
                      </span>
                    )}
                    {member.mobile && (
                      <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                        <Phone className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-gray-600">{member.mobile}</span>
                      </span>
                    )}
                    {member.location && (
                      <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                        <MapPin className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-gray-600">{member.location}</span>
                      </span>
                    )}
                    {member.industry && (
                      <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                        <Briefcase className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-gray-600">{member.industry}</span>
                      </span>
                    )}
                    {(member.registrationDate || member.createdAt) && (
                      <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                        <Calendar className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-gray-600">
                          {formatDate(member.registrationDate || member.createdAt)}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-2 flex-shrink-0 w-full lg:w-auto lg:justify-end">
                  {/* View Details - Always Available */}
                  <button 
                    onClick={() => handleViewDetails(member)} 
                    className="p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-all shadow-sm group-hover:shadow-md"
                    title="View details"
                  >
                    <Eye className="h-5 w-5 text-blue-600" />
                  </button>

                  {/* Approve & Reject - Only for Pending Approval */}
                  {member.approvalStatus === 'Pending' && (
                    <>
                      <button 
                        onClick={() => handleApprove(member)} 
                        className="p-3 border-2 border-green-200 rounded-lg hover:bg-green-50 hover:border-green-400 transition-all shadow-sm group-hover:shadow-md"
                        title="Approve member"
                      >
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      </button>
                      <button 
                        onClick={() => handleReject(member)} 
                        className="p-3 border-2 border-red-200 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all shadow-sm group-hover:shadow-md"
                        title="Reject member"
                      >
                        <XCircle className="h-5 w-5 text-red-600" />
                      </button>
                    </>
                  )}

                  {/* Activate - Only for Approved but Not Active */}
                  {member.approvalStatus === 'Approved' && member.membershipStatus !== 'Active' && (
                    <button 
                      onClick={() => handleActivate(member)} 
                      className="p-3 border-2 border-green-200 rounded-lg hover:bg-green-50 hover:border-green-400 transition-all shadow-sm group-hover:shadow-md"
                      title="Activate membership"
                    >
                      <UserCheck className="h-5 w-5 text-green-600" />
                    </button>
                  )}

                  {/* Deactivate - Only for Active Members */}
                  {member.membershipStatus === 'Active' && (
                    <button 
                      onClick={() => handleDeactivate(member)} 
                      className="p-3 border-2 border-orange-200 rounded-lg hover:bg-orange-50 hover:border-orange-400 transition-all shadow-sm group-hover:shadow-md"
                      title="Deactivate membership"
                    >
                      <Ban className="h-5 w-5 text-orange-600" />
                    </button>
                  )}

                  {/* Create Cash Payment - Only for Pending Payment */}
                  {(member.paymentStatus === 'pending' || member.paymentStatus === 'Pending') && (
                    <button 
                      onClick={() => {
                        setSelectedMember(member);
                        setShowCashPaymentModal(true);
                      }}
                      className="p-3 border-2 border-purple-200 rounded-lg hover:bg-purple-50 hover:border-purple-400 transition-all shadow-sm group-hover:shadow-md"
                      title="Create cash payment"
                    >
                      <CreditCard className="h-5 w-5 text-purple-600" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total || members.length} total)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Member Details Modal */}
      {showDetails && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-end z-[1000]">
          <div className="bg-white shadow-2xl w-full sm:w-[92vw] md:w-[80vw] lg:w-[68vw] xl:w-[58vw] 2xl:w-[50vw] max-w-[860px] h-full sm:h-[calc(100vh-32px)] sm:my-4 overflow-y-auto overflow-x-hidden rounded-none sm:rounded-2xl min-w-0">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between gap-4 z-10">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                Member Details
              </h3>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedMember(null);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div className="p-4 sm:p-6 lg:p-8">
              {fieldGroups.map((group, groupIdx) => (
                <div key={groupIdx} className={groupIdx > 0 ? 'mt-8' : ''}>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    {group.title}
                  </h4>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {group.fields.map((field) => {
                      const value = selectedMember[field.key];
                      const displayValue = field.format 
                        ? field.format(value) 
                        : formatMemberValue(value);
                      
                      // Special handling for status fields
                      if (field.key === 'approvalStatus' || field.key === 'membershipStatus') {
                        return (
                          <div key={field.key}>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                              {field.label}
                            </label>
                            <div className="mt-1">{getStatusBadge(value || (field.key === 'approvalStatus' ? 'Pending' : 'Inactive'))}</div>
                          </div>
                        );
                      }

                      // Special handling for services array
                      if (field.key === 'services' && Array.isArray(value) && value.length > 0) {
                        return (
                          <div key={field.key} className="xl:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                              {field.label}
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {value.map((service, idx) => (
                                <span key={idx} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                                  {service}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      // Special handling for photo URL
                      if (field.key === 'photo' && value) {
                        return (
                          <div key={field.key} className="xl:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                              {field.label}
                            </label>
                            <img 
                              src={value} 
                              alt="Member photo" 
                              className="max-w-full sm:max-w-xs h-auto rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        );
                      }

                      return (
                        <div key={field.key}>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            {field.label}
                          </label>
                          <p className="text-gray-900 break-words">{displayValue}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer with Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Approve Button - Only for Pending Approval */}
                {selectedMember.approvalStatus === 'Pending' && (
                  <button
                    onClick={async () => {
                      await handleApprove(selectedMember);
                      // Don't close modal, let user see updated status
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 hover:text-black transition-colors text-sm font-semibold w-full sm:w-auto"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Approve Member
                  </button>
                )}

                {/* Show "Approved" text when already approved */}
                {selectedMember.approvalStatus === 'Approved' && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-semibold w-full sm:w-auto">
                    <CheckCircle2 className="h-4 w-4" />
                    Approved
                  </span>
                )}

                {/* Activate Button - Only for Approved but Not Active */}
                {selectedMember.approvalStatus === 'Approved' && selectedMember.membershipStatus !== 'Active' && (
                  <button
                    onClick={async () => {
                      await handleActivate(selectedMember);
                      // Don't close modal, let user see updated status
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:text-black transition-colors text-sm font-semibold w-full sm:w-auto"
                  >
                    <UserCheck className="h-4 w-4" />
                    Activate Member
                  </button>
                )}

                {/* Create Cash Payment - Only for Pending Payment */}
                {(selectedMember.paymentStatus === 'pending' || selectedMember.paymentStatus === 'Pending') && (
                  <button
                    onClick={() => {
                      setShowCashPaymentModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 hover:text-black transition-colors text-sm font-semibold w-full sm:w-auto"
                  >
                    <CreditCard className="h-4 w-4" />
                    Create Cash Payment
                  </button>
                )}

                {/* Reject Button - Only for Pending Approval */}
                {selectedMember.approvalStatus === 'Pending' && (
                  <button
                    onClick={async () => {
                      await handleReject(selectedMember);
                      // Don't close modal, let user see updated status
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 hover:text-black transition-colors text-sm font-semibold w-full sm:w-auto"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject Member
                  </button>
                )}

                {/* Deactivate Button - Only for Active Members */}
                {selectedMember.membershipStatus === 'Active' && (
                  <button
                    onClick={async () => {
                      await handleDeactivate(selectedMember);
                      // Reload member details
                      const updatedMember = await getMember(selectedMember._id);
                      setSelectedMember(updatedMember);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 hover:text-black transition-colors text-sm font-semibold w-full sm:w-auto"
                  >
                    <Ban className="h-4 w-4" />
                    Deactivate Membership
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedMember(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium w-full sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cash Payment Modal */}
      {showCashPaymentModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-end z-[1000]">
          <div className="bg-white shadow-2xl w-full sm:w-[66vw] md:w-[52vw] lg:w-[40vw] xl:w-[34vw] 2xl:w-[30vw] max-w-[520px] h-full sm:h-[calc(100vh-32px)] sm:my-4 overflow-y-auto overflow-x-hidden rounded-none sm:rounded-2xl min-w-0">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base sm:text-xl font-bold text-gray-900 break-words">
                Create Cash Payment for {selectedMember.personName || selectedMember.organizationName}
              </h3>
              <button
                onClick={() => {
                  setShowCashPaymentModal(false);
                  setCashPaymentAmount('');
                  setCashPaymentDescription('');
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter amount"
                  value={cashPaymentAmount}
                  onChange={(e) => setCashPaymentAmount(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Payment Description (optional)
                </label>
                <textarea
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows="3"
                  placeholder="Payment description"
                  value={cashPaymentDescription}
                  onChange={(e) => setCashPaymentDescription(e.target.value)}
                />
              </div>
            </div>
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCashPaymentModal(false);
                  setCashPaymentAmount('');
                  setCashPaymentDescription('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCashPayment}
                disabled={creatingPayment || !cashPaymentAmount || parseFloat(cashPaymentAmount) <= 0}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
              >
                {creatingPayment ? 'Creating...' : 'Create Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMembersPage;

